import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "../../styles/AnnouncementCenter.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const today = () => {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
};

const emptyForm = () => ({
  id: null,
  announcementDate: today(),
  type: "update",
  audience: "both",
  title: "",
  content: "- ",
  isActive: true,
  maintenanceStartTime: "18:30",
  maintenanceDurationMinutes: 15,
});

function label(type) {
  return type === "maintenance" ? "Maintenance" : type === "update" ? "Update" : "Notice";
}

function audienceLabel(audience) {
  if (audience === "admin") return "Admins only";
  if (audience === "user") return "Users only";
  return "Users & Admins";
}

function nextVersion(rows) {
  const versions = rows.map((row) => String(row.title || "").match(/ver\.\s*(\d+)(?:\.(\d+))?/i))
    .filter(Boolean).map((match) => [Number(match[1]), Number(match[2] || 0)]);
  if (!versions.length) return "1.0";
  versions.sort((a, b) => b[0] - a[0] || b[1] - a[1]);
  return `${versions[0][0]}.${versions[0][1] + 1}`;
}

function defaultsFor(type, rows, form) {
  if (type === "update") return { title: `Update : Ver.${nextVersion(rows)}`, content: "- " };
  if (type === "maintenance") {
    return {
      title: "Scheduled Maintenance Notice",
      content: `The system will be under maintenance at ${formatTime(form.maintenanceStartTime)} Mountain Time. The maintenance is expected to take approximately ${form.maintenanceDurationMinutes} minutes. During this time, the portal will be temporarily unavailable. Thank you for your patience.`,
    };
  }
  return { title: "System Notice", content: "" };
}

function formatTime(value) {
  if (!value) return "";
  const [hour, minute] = value.split(":").map(Number);
  return `${hour % 12 || 12}:${String(minute).padStart(2, "0")} ${hour >= 12 ? "p.m." : "a.m."}`;
}

export default function AnnouncementCenter() {
  const [form, setForm] = useState(emptyForm);
  const [announcements, setAnnouncements] = useState([]);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [manualMaintenance, setManualMaintenance] = useState(false);
  const [scheduledMaintenance, setScheduledMaintenance] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const contentRef = useRef(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/api/announcements`);
      const rows = Array.isArray(data.announcements) ? data.announcements : [];
      setAnnouncements(rows);
      setManualMaintenance(Boolean(data.maintenanceModeManuallyEnabled));
      setScheduledMaintenance(Boolean(data.scheduledMaintenanceModeEnabled));
      setMaintenanceMessage(data.maintenanceMessage || "");
      setForm((current) => current.title ? current : { ...current, ...defaultsFor(current.type, rows, current) });
      setError("");
      return rows;
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to load Announcement Center.");
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  function update(name, value) {
    setForm((current) => {
      const next = { ...current, [name]: value };
      if (name === "type" && !current.id) Object.assign(next, defaultsFor(value, announcements, next));
      if (next.type === "maintenance" && ["maintenanceStartTime", "maintenanceDurationMinutes"].includes(name)) {
        next.content = defaultsFor("maintenance", announcements, next).content;
      }
      return next;
    });
  }

  function reset(rows = announcements) {
    const next = emptyForm();
    setForm({ ...next, ...defaultsFor("update", rows, next) });
  }

  function handleContentKeyDown(event) {
    if (event.key !== "Enter" || event.shiftKey) return;
    const input = event.currentTarget;
    const line = form.content.slice(0, input.selectionStart).split("\n").pop();
    const bullet = line.match(/^(\s*[-*]\s+)/);
    if (!bullet) return;
    event.preventDefault();
    const position = input.selectionStart + bullet[1].length + 1;
    update("content", `${form.content.slice(0, input.selectionStart)}\n${bullet[1]}${form.content.slice(input.selectionEnd)}`);
    requestAnimationFrame(() => contentRef.current?.setSelectionRange(position, position));
  }

  async function submit(event) {
    event.preventDefault();
    setSaving(true); setMessage(""); setError("");
    try {
      const payload = { ...form, maintenanceDurationMinutes: Number(form.maintenanceDurationMinutes) };
      const { data } = form.id
        ? await axios.put(`${API_BASE}/api/announcements/${form.id}`, payload)
        : await axios.post(`${API_BASE}/api/announcements`, payload);
      setMessage(data.message || "Announcement saved successfully.");
      const nextRows = await load();
      reset(nextRows || announcements);
      window.dispatchEvent(new Event("announcements-changed"));
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to save the announcement.");
    } finally { setSaving(false); }
  }

  async function toggleAnnouncement(row) {
    setSaving(true); setMessage(""); setError("");
    try {
      const { data } = await axios.patch(`${API_BASE}/api/announcements/${row.id}/status`, { isActive: !row.isActive });
      setMessage(data.message);
      await load();
      window.dispatchEvent(new Event("announcements-changed"));
    } catch (requestError) { setError(requestError.response?.data?.message || "Unable to update the announcement."); }
    finally { setSaving(false); }
  }

  async function saveMaintenance(payload, successMessage) {
    setSaving(true); setMessage(""); setError("");
    try {
      const { data } = await axios.put(`${API_BASE}/api/announcements/maintenance/settings`, payload);
      setManualMaintenance(Boolean(data.maintenanceModeManuallyEnabled));
      setScheduledMaintenance(Boolean(data.scheduledMaintenanceModeEnabled));
      setMaintenanceMessage(data.maintenanceMessage || "");
      setMessage(successMessage);
      window.dispatchEvent(new Event("announcements-changed"));
    } catch (requestError) { setError(requestError.response?.data?.message || "Unable to update maintenance mode."); }
    finally { setSaving(false); }
  }

  function edit(row) {
    setForm({
      id: row.id,
      announcementDate: row.announcementDate || today(),
      type: row.type || "notice",
      audience: row.audience || "both",
      title: row.title || "",
      content: row.content || "",
      isActive: Boolean(row.isActive),
      maintenanceStartTime: row.maintenanceStartTime || "18:30",
      maintenanceDurationMinutes: row.maintenanceDurationMinutes || 15,
    });
    document.querySelector(".announcement-create-section")?.scrollIntoView({ behavior: "smooth" });
  }

  return (
    <div className="account-settings-tab-panel announcement-center-panel">
      <div className="account-settings-tab-header">
        <div><h2>Announcement Center</h2><p>Manage updates, maintenance announcements, the global banner, and maintenance mode.</p></div>
        <span className="announcement-system-pill">System</span>
      </div>
      {message && <div className="announcement-feedback success">{message}</div>}
      {error && <div className="announcement-feedback error">{error}</div>}
      {loading ? <p>Loading announcement settings...</p> : <>
        <section className="announcement-admin-section">
          <div className="announcement-section-heading"><h3>Maintenance Mode</h3><p>Show a maintenance screen to all users except accounts assigned the Super Admin security role.</p></div>
          <label className="announcement-toggle-row">
            <span><strong>Maintenance screen</strong><small>{scheduledMaintenance ? "Currently controlled by an active maintenance schedule." : manualMaintenance ? "Turn this off when maintenance is complete." : "All non-admin users will see only the maintenance screen."}</small></span>
            <input type="checkbox" checked={manualMaintenance || scheduledMaintenance} disabled={scheduledMaintenance && !manualMaintenance} onChange={(event) => {
              const enabled = event.target.checked;
              if (window.confirm(`Are you sure you want to turn ${enabled ? "on" : "off"} maintenance mode?`)) {
                saveMaintenance({ maintenanceModeEnabled: enabled }, `Maintenance mode turned ${enabled ? "on" : "off"}.`);
              }
            }} />
          </label>
          <label className="announcement-field full"><span>Maintenance Message</span><textarea rows="4" value={maintenanceMessage} onChange={(event) => setMaintenanceMessage(event.target.value)} /></label>
          <button type="button" disabled={saving} onClick={() => saveMaintenance({ maintenanceMessage }, "Maintenance message saved.")}>Save Maintenance Message</button>
        </section>

        <section className="announcement-admin-section announcement-create-section">
          <div className="announcement-section-heading"><h3>{form.id ? "Edit Announcement" : "Create Announcement"}</h3><p>Choose whether each announcement is shown to users, administrators, or both audiences.</p></div>
          <form className="announcement-form-grid" onSubmit={submit}>
            <label className="announcement-field"><span>Date</span><input type="date" required value={form.announcementDate} onChange={(event) => update("announcementDate", event.target.value)} /></label>
            <label className="announcement-field"><span>Type</span><select value={form.type} onChange={(event) => update("type", event.target.value)}><option value="update">Update</option><option value="maintenance">Maintenance</option><option value="notice">Notice</option></select></label>
            <label className="announcement-field"><span>Audience</span><select value={form.audience} onChange={(event) => update("audience", event.target.value)}><option value="both">Both (Users &amp; Admins)</option><option value="user">Users only</option><option value="admin">Admins only</option></select></label>
            {form.type === "maintenance" && <>
              <label className="announcement-field"><span>Start Time (Mountain Time)</span><input type="time" required value={form.maintenanceStartTime} onChange={(event) => update("maintenanceStartTime", event.target.value)} /></label>
              <label className="announcement-field"><span>Duration (minutes)</span><input type="number" min="1" max="1440" required value={form.maintenanceDurationMinutes} onChange={(event) => update("maintenanceDurationMinutes", event.target.value)} /></label>
            </>}
            <label className="announcement-field full"><span>Title</span><input required maxLength="255" value={form.title} onChange={(event) => update("title", event.target.value)} /></label>
            <label className="announcement-field full"><span>Content</span><textarea ref={contentRef} rows="5" required value={form.content} onKeyDown={handleContentKeyDown} onChange={(event) => update("content", event.target.value)} /></label>
            <label className="announcement-toggle-row full"><span><strong>Active now</strong><small>Make this announcement visible immediately.</small></span><input type="checkbox" checked={form.isActive} onChange={(event) => update("isActive", event.target.checked)} /></label>
            <div className="announcement-form-actions full"><button type="submit" disabled={saving}>{form.id ? "Update Announcement" : "Create Announcement"}</button><button type="button" className="secondary" onClick={() => reset()}>Cancel</button></div>
          </form>
        </section>

        <section className="announcement-admin-section">
          <div className="announcement-section-heading"><h3>Announcement History</h3><p>Edit a previous record or turn it on and off at any time.</p></div>
          <div className="announcement-history">
            {!announcements.length ? <p>No announcements yet.</p> : announcements.map((row) => <article key={row.id} className={row.isActive ? "active" : ""}>
              <div className="announcement-history-head"><div><div className="announcement-history-meta"><span>{row.announcementDate}</span><span className={`announcement-type ${row.type}`}>{label(row.type)}</span><span className={`announcement-audience ${row.audience || "both"}`}>{audienceLabel(row.audience)}</span><span className={`announcement-status ${row.isActive ? "on" : ""}`}>{row.isActive ? "On" : "Off"}</span></div>{row.type === "maintenance" && <small>{formatTime(row.maintenanceStartTime)} Mountain Time &middot; {row.maintenanceDurationMinutes} minutes</small>}<h4>{row.title}</h4></div><div><button type="button" className="secondary" onClick={() => edit(row)}>Edit</button><button type="button" disabled={saving} onClick={() => toggleAnnouncement(row)}>Turn {row.isActive ? "Off" : "On"}</button></div></div>
              <p>{row.content}</p>
            </article>)}
          </div>
        </section>
      </>}
    </div>
  );
}
